import { useEffect, useState } from 'react'
import { PageHeader, StateBlock } from '../../shared/StateBlock'
import { ibApi, IbApiError, type IbSubadmin } from '../api'

export function IbDashboardPage() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [subadmin, setSubadmin] = useState<IbSubadmin | null>(null)
  const [stats, setStats] = useState({
    clientCount: 0,
    balance: 0,
    minimumReserve: 0,
    withdrawable: 0,
    totalCommission: 0,
    withdrawableCommission: 0,
    pendingCommission: 0,
  })
  const [referralLink, setReferralLink] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    void (async () => {
      setLoading(true)
      setError('')
      try {
        const data = await ibApi.dashboard()
        setSubadmin(data.subadmin)
        setStats({
          clientCount: data.stats.clientCount || 0,
          balance: data.stats.balance || 0,
          minimumReserve: data.stats.minimumReserve || 0,
          withdrawable: data.stats.withdrawable || 0,
          totalCommission: data.stats.totalCommission || 0,
          withdrawableCommission: data.stats.withdrawableCommission || 0,
          pendingCommission: data.stats.pendingCommission || 0,
        })
        setReferralLink(data.referralLink || data.subadmin.referralLink || '')
      } catch (err) {
        setError(err instanceof IbApiError ? err.message : 'Unable to load dashboard')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  async function copyLink() {
    if (!referralLink) return
    try {
      await navigator.clipboard.writeText(referralLink)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setError('Unable to copy link')
    }
  }

  return (
    <div>
      <PageHeader eyebrow="IB portal" title="Dashboard">
        <p className="cp-muted">
          View-only clients, wallet, and PREDGOLD net exposure for your book.
        </p>
      </PageHeader>

      {loading ? (
        <p className="cp-muted ap-inline-status" aria-live="polite">
          Loading dashboard…
        </p>
      ) : null}
      {error ? (
        <StateBlock tone="error" title="Error">
          {error}
        </StateBlock>
      ) : null}

      <div className={loading ? 'is-refreshing' : undefined}>
        <div className="cp-kpi-grid">
          <article className="cp-kpi">
            <span>Clients</span>
            <strong>{loading ? '—' : stats.clientCount}</strong>
          </article>
          <article className="cp-kpi">
            <span>Total commission</span>
            <strong>{loading ? '—' : `$${stats.totalCommission.toFixed(2)}`}</strong>
            <em>
              {loading
                ? 'Pending —'
                : `Pending $${stats.pendingCommission.toFixed(2)}`}
            </em>
          </article>
          <article className="cp-kpi">
            <span>Withdrawable commission</span>
            <strong>
              {loading ? '—' : `$${stats.withdrawableCommission.toFixed(2)}`}
            </strong>
            <em>
              {loading
                ? 'Hold —'
                : `Unlocks after ${subadmin?.commissionHoldDays ?? 14} days`}
            </em>
          </article>
          <article className="cp-kpi">
            <span>Wallet withdrawable</span>
            <strong>{loading ? '—' : `$${stats.withdrawable.toFixed(2)}`}</strong>
            <em>
              {loading
                ? 'Balance —'
                : `Balance $${stats.balance.toFixed(2)} · reserve $${stats.minimumReserve.toFixed(2)}`}
            </em>
          </article>
        </div>

        <section className="cp-panel" style={{ marginTop: '1.25rem' }}>
          <h2>Your terms</h2>
          <p className="cp-muted">
            Code <strong>{subadmin?.code || '—'}</strong> · Deposit commission{' '}
            {subadmin?.depositCommissionPercent ?? 0}% · Hold{' '}
            {subadmin?.commissionHoldDays ?? 14} days · Profit share{' '}
            {subadmin?.profitSharePercent ?? '—'}% · Loss share{' '}
            {subadmin?.lossSharePercent ?? '—'}% · MT5 tag{' '}
            <code>{subadmin?.mt5CommentTag || '—'}</code>
          </p>
          <label className="cp-field">
            <span className="cp-field__label">Client register link</span>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input value={referralLink} readOnly style={{ flex: 1, minWidth: '16rem' }} />
              <button
                type="button"
                className="pp-btn pp-btn--secondary"
                onClick={() => void copyLink()}
                disabled={loading || !referralLink}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </label>
        </section>
      </div>
    </div>
  )
}
